import { useState, useEffect, useMemo } from 'react';
import { Search, Filter, Database, FileText, Download, MoreVertical, ChevronLeft, ChevronRight, Plus, ExternalLink, FileSpreadsheet, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { ScrollArea } from './ui/scroll-area';
import { Badge } from './ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { Card, CardContent } from './ui/card';
import { useSessionStore, useNotificationStore } from '@/stores';
import { useJobPollingStore } from '@/stores/jobPollingStore';
import { USApplicationDisplay } from './uspto/USApplicationDisplay';
import { ExportModal } from './uspto/ExportModal';
import { documentApi, patentApi } from '@/services/api';
import { addLoadingDocument, removeLoadingDocument } from '@/utils/loadingDocuments';

interface USPTODataPageProps {
  onDocumentSelect?: (doc: any) => void;
}

interface ApplicationResource {
  id: string;
  type: 'application';
  number: string;
  data: {
    application_number: string;
    patent_info: {
      applicationMetaData: any;
    };
    pgpubDocumentMetaData?: any;
    grantDocumentMetaData?: any;
  };
}

export function USPTODataPage({ onDocumentSelect }: USPTODataPageProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedResource, setSelectedResource] = useState<ApplicationResource | null>(null);
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddInput, setShowAddInput] = useState(false);
  const [newApplicationNumber, setNewApplicationNumber] = useState('');
  const [refreshingApps, setRefreshingApps] = useState<Set<string>>(new Set());

  const sessionApplications = useSessionStore(state => state.sessionApplications);
  const setSessionApplications = useSessionStore(state => state.setSessionApplications);
  const { addNotification } = useNotificationStore();
  const { startPolling } = useJobPollingStore();

  // Handle initial loading state
  useEffect(() => {
    // If we have applications, stop loading immediately
    if (sessionApplications && sessionApplications.length > 0) {
      setIsLoading(false);
    } else {
      // Give it 2 seconds to load, then show "no data" message
      const timer = setTimeout(() => {
        setIsLoading(false);
      }, 2000);

      return () => clearTimeout(timer);
    }
  }, [sessionApplications]);

  // Listen for job completion events
  useEffect(() => {
    const handleJobComplete = (event: CustomEvent) => {
      const { jobId, docType, docId, success, error } = event.detail;
      const resourceTypeName = docType.charAt(0).toUpperCase() + docType.slice(1);

      if (success) {
        addNotification({
          type: 'success',
          title: `${resourceTypeName} Ready`,
          message: `${docId} has been loaded and added to your session.`,
        });
        removeLoadingDocument(docId, resourceTypeName);

        // Refresh session_applications from backend after successful load
        setTimeout(async () => {
          try {
            const freshApplications = await patentApi.getSessionApplications(true); // Override cache
            setSessionApplications(freshApplications);
            window.dispatchEvent(new Event('storage-update'));
          } catch (error) {
            console.error('Failed to refresh session applications:', error);
            // Still trigger update even if refresh fails
            window.dispatchEvent(new Event('storage-update'));
          }
        }, 500);
      } else {
        let errorMessage = `Failed to load ${docId}. Please try again.`;

        if (error === '404') {
          errorMessage = `${docId} not found. This may be an older document. Please verify the number is correct.`;
        } else if (error === 'timeout') {
          addNotification({
            type: 'warning',
            title: 'Loading Timeout',
            message: `${docId} loading timed out. It may still complete in the background.`,
            duration: 5000,
          });
          return;
        } else if (error === 'polling-error') {
          errorMessage = `Error checking status for ${docId}.`;
        }

        addNotification({
          type: 'error',
          title: `${resourceTypeName} Load Failed`,
          message: errorMessage,
          duration: 8000,
        });

        removeLoadingDocument(docId, resourceTypeName);

        // Remove from context
        const stored = localStorage.getItem('session_context');
        const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};

        if (docType === 'application') {
          context.current_applications = (context.current_applications || []).filter((id: string) => id !== docId);
        }

        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));
      }
    };

    window.addEventListener('job-complete', handleJobComplete as EventListener);

    return () => {
      window.removeEventListener('job-complete', handleJobComplete as EventListener);
    };
  }, [addNotification]);

  // Listen for storage updates to refresh the application list
  useEffect(() => {
    const handleStorageUpdate = () => {
      // Force component to re-read sessionApplications from store
      setIsLoading(true);
      setTimeout(() => setIsLoading(false), 0);
    };

    window.addEventListener('storage-update', handleStorageUpdate);

    return () => {
      window.removeEventListener('storage-update', handleStorageUpdate);
    };
  }, []);

  const pollJobStatus = (jobId: string, docType: string, docId: string) => {
    // Use global polling store that persists across navigation
    startPolling(jobId, docType, docId);
  };

  const addToActiveDocuments = async (applicationNumber: string) => {
    try {
      // Clean the application number - remove prefixes
      let cleanedId = applicationNumber
        .replace(/^(patent-|publication-|application-)/, '')
        .replace(/,/g, '')
        .replace(/\//g, '')
        .replace(/^US/i, '')
        .replace(/[A-Z]\d*$/i, '');

      // Check if already in context
      const stored = localStorage.getItem('session_context');
      const context = stored && stored !== "undefined" ? JSON.parse(stored) : {};
      const currentApps = Array.isArray(context.current_applications) ? context.current_applications : [];

      if (currentApps.includes(cleanedId)) {
        addNotification({
          type: 'info',
          title: 'Already Added',
          message: `${cleanedId} is already in your active documents.`,
          duration: 3000,
        });
        return;
      }

      // Call the API to fetch and load the application
      const response = await documentApi.fetchAndLoadDocument('application', cleanedId);

      if (response?.job_id) {
        // Add to session context immediately
        context.current_applications = [...currentApps, cleanedId];
        localStorage.setItem('session_context', JSON.stringify(context));
        window.dispatchEvent(new Event('storage-update'));

        addNotification({
          type: 'info',
          title: 'Loading Application',
          message: `Processing ${cleanedId}...`,
          duration: 60000,
        });

        // Add to loading documents tracking
        addLoadingDocument(cleanedId, 'Application', cleanedId);

        // Start polling for job status
        pollJobStatus(response.job_id, 'application', cleanedId);
      }
    } catch (error) {
      console.error('Failed to add to active documents:', error);
      addNotification({
        type: 'error',
        title: 'Failed to Add',
        message: 'Failed to add application to active documents. Please try again.',
        duration: 5000,
      });
    }
  };

  const handleAddNewApplication = async () => {
    if (!newApplicationNumber.trim()) {
      addNotification({
        type: 'error',
        title: 'Invalid Input',
        message: 'Please enter an application number.',
        duration: 3000,
      });
      return;
    }

    await addToActiveDocuments(newApplicationNumber);
    setNewApplicationNumber('');
    setShowAddInput(false);
  };

  const handleRefreshApplication = async (applicationNumber: string) => {
    // Prevent multiple refreshes of the same application
    if (refreshingApps.has(applicationNumber)) return;

    setRefreshingApps(prev => new Set([...prev, applicationNumber]));

    try {
      addNotification({
        type: 'info',
        title: 'Refreshing Application',
        message: `Fetching latest data for ${applicationNumber} from USPTO...`,
        duration: 3000,
      });

      // Refresh the specific application from backend
      await patentApi.refreshApplication(applicationNumber);

      // Clear localStorage cache for this application
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && (
          key.includes(`pros_info-${applicationNumber}`) ||
          key.includes(`pros_doc-${applicationNumber}`) ||
          key.includes(`app_info-${applicationNumber}`)
        )) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach(key => localStorage.removeItem(key));

      // After refresh, reload session applications to get updated data (force override cache)
      const freshApplications = await patentApi.getSessionApplications(true);
      setSessionApplications(freshApplications);

      // Update session_applications in localStorage if the store doesn't do it automatically
      localStorage.setItem('session_applications', JSON.stringify(freshApplications));

      // Trigger storage update event to refresh other components
      window.dispatchEvent(new Event('storage-update'));

      addNotification({
        type: 'success',
        title: 'Refresh Complete',
        message: `${applicationNumber} has been updated with latest USPTO data.`,
        duration: 3000,
      });
    } catch (error) {
      console.error('Failed to refresh application:', error);
      addNotification({
        type: 'error',
        title: 'Refresh Failed',
        message: `Failed to refresh ${applicationNumber}. Please try again.`,
        duration: 5000,
      });
    } finally {
      setRefreshingApps(prev => {
        const newSet = new Set(prev);
        newSet.delete(applicationNumber);
        return newSet;
      });
    }
  };

  // Create resources from sessionApplications
  const usptoResources = useMemo(() => {
    const resources: ApplicationResource[] = [];

    if (sessionApplications?.length > 0) {
      sessionApplications.forEach((sessionApp: any) => {
        const appNumber = sessionApp.application_id || sessionApp.application?.applicationNumberText;

        // Filter out null/empty applications
        const hasValidData = sessionApp.application &&
          sessionApp.application.applicationMetaData &&
          sessionApp.application.applicationMetaData.inventionTitle &&
          sessionApp.application.applicationMetaData.inventionTitle !== 'null';

        if (appNumber && hasValidData) {
          resources.push({
            id: `app-${appNumber}`,
            type: 'application',
            number: appNumber,
            data: {
              application_number: appNumber,
              patent_info: {
                applicationMetaData: sessionApp.application?.applicationMetaData || {}
              },
              pgpubDocumentMetaData: sessionApp.application?.pgpubDocumentMetaData,
              grantDocumentMetaData: sessionApp.application?.grantDocumentMetaData,
            }
          });
        }
      });
    }

    return resources;
  }, [sessionApplications]);

  // Helper functions
  const getResourceTitle = (resource: ApplicationResource) => {
    const metaData = resource.data?.patent_info?.applicationMetaData;
    return metaData?.inventionTitle || `Application ${resource.number}`;
  };

  const getResourceStatus = (resource: ApplicationResource) => {
    const metaData = resource.data?.patent_info?.applicationMetaData;
    const statusText = metaData?.applicationStatusDescriptionText?.toLowerCase() || '';

    // Check for abandoned status
    if (statusText.includes('abandon')) {
      return 'Abandoned';
    }

    // Check for expired status
    if (statusText.includes('expired')) {
      return 'Expired';
    }

    // Check for granted/patented status
    if (metaData?.grantDate || metaData?.patentNumber || statusText.includes('patent')) {
      return 'Granted';
    }

    // Default to pending
    return 'Pending';
  };

  // Filter resources based on search term and status
  const filteredResources = useMemo(() => {
    if (!usptoResources.length) return [];

    return usptoResources.filter(resource => {
      const data = resource.data;
      const metaData = data?.patent_info?.applicationMetaData;

      // Search filter
      const searchTerm = searchQuery.toLowerCase().trim();
      if (searchTerm) {
        const searchableFields = [
          resource.number,
          metaData?.inventionTitle,
          metaData?.firstInventorName,
          metaData?.assigneeName,
          metaData?.docketNumber,
          metaData?.patentNumber,
          metaData?.earliestPublicationNumber,
        ].filter(Boolean).map(field => String(field).toLowerCase());

        if (!searchableFields.some(field => field.includes(searchTerm))) {
          return false;
        }
      }

      // Status filter
      if (filterStatus !== 'all') {
        const resourceStatus = getResourceStatus(resource).toLowerCase();
        if (resourceStatus !== filterStatus) return false;
      }

      return true;
    });
  }, [usptoResources, searchQuery, filterStatus]);

  // Auto-select first resource
  useEffect(() => {
    if (filteredResources.length > 0 && !selectedResource) {
      setSelectedResource(filteredResources[0]);
    }
  }, [filteredResources, selectedResource]);

  const highlightText = (text: string | null | undefined, searchTerm: string) => {
    if (!searchTerm.trim() || !text) return text;

    const regex = new RegExp(`(${searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
    const parts = String(text).split(regex);

    return parts.map((part, index) =>
      regex.test(part) ? (
        <span key={index} className="bg-yellow-200 text-gray-900 px-1 rounded">
          {part}
        </span>
      ) : part
    );
  };

  // Show loading state while fetching session applications
  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50">
        <Database className="w-16 h-16 text-gray-400 mb-4 animate-pulse" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Loading USPTO Data...</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Fetching your session applications
        </p>
      </div>
    );
  }

  if (usptoResources.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-full bg-gray-50">
        <Database className="w-16 h-16 text-gray-400 mb-4" />
        <h3 className="text-lg font-semibold text-gray-900 mb-2">No USPTO Data Available</h3>
        <p className="text-sm text-gray-500 text-center max-w-md">
          Add patent applications to your session to view detailed USPTO data here.
          Use the chat to add applications by their application number.
        </p>
      </div>
    );
  }

  return (
    <div className="flex h-full bg-gray-50">
      {/* Sidebar - Resource List */}
      <div className={`flex-shrink-0 h-full border-r border-gray-200 bg-white overflow-y-auto transition-all duration-300 ${
        isCollapsed ? 'w-12' : 'w-80'
      }`}>
        {/* Header */}
        <div className="border-b border-gray-200 px-4 py-3 bg-white sticky top-0 z-10">
          <div className="flex items-center justify-between mb-3">
            {!isCollapsed && (
              <h2 className="text-lg font-semibold text-gray-900">USPTO Applications</h2>
            )}
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => setIsCollapsed(!isCollapsed)}
            >
              {isCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
            </Button>
          </div>

          {!isCollapsed && (
            <>
              {/* Add Application Button */}
              {!showAddInput && (
                <div className="mb-3">
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowAddInput(true)}
                    className="gap-2 w-full"
                  >
                    <Plus className="w-4 h-4" />
                    Add Application
                  </Button>
                </div>
              )}

              {/* Add Application Input */}
              {showAddInput && (
                <div className="mb-3 space-y-2">
                  <Input
                    type="text"
                    placeholder="Application number (e.g., 17123456)"
                    value={newApplicationNumber}
                    onChange={(e) => setNewApplicationNumber(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        handleAddNewApplication();
                      } else if (e.key === 'Escape') {
                        setShowAddInput(false);
                        setNewApplicationNumber('');
                      }
                    }}
                    className="w-full"
                    autoFocus
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      onClick={handleAddNewApplication}
                      className="flex-1"
                    >
                      Add
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => {
                        setShowAddInput(false);
                        setNewApplicationNumber('');
                      }}
                      className="flex-1"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}

              <p className="text-sm text-gray-500 mb-3">
                {searchQuery.trim() || filterStatus !== 'all'
                  ? `${filteredResources.length} of ${usptoResources.length} applications`
                  : `${usptoResources.length} application${usptoResources.length !== 1 ? 's' : ''}`
                }
              </p>

              {/* Search Input */}
              <div className="relative mb-3">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search applications..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                />
              </div>

              {/* Status Filter */}
              <Select value={filterStatus} onValueChange={setFilterStatus}>
                <SelectTrigger className="w-full">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Applications</SelectItem>
                  <SelectItem value="granted">Granted</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="abandoned">Abandoned</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                </SelectContent>
              </Select>

              {/* Export Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowExportModal(true)}
                disabled={usptoResources.length === 0}
                className="gap-2 w-full mt-3"
              >
                <FileSpreadsheet className="w-4 h-4" />
                Export to Excel
              </Button>
            </>
          )}
        </div>

        {/* Application List */}
        {!isCollapsed && (
          <div className="p-2">
            {filteredResources.length > 0 ? (
              filteredResources.map((resource) => {
                const metaData = resource.data?.patent_info?.applicationMetaData;
                const status = getResourceStatus(resource);

                return (
                  <div
                    key={resource.id}
                    className={`w-full p-3 mb-2 rounded-lg border transition-colors ${
                      selectedResource?.id === resource.id
                        ? 'bg-blue-50 border-blue-200'
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => setSelectedResource(resource)}
                    >
                      <div className="flex justify-between items-start mb-2">
                        <Badge
                          variant="outline"
                          className={
                            status === 'Granted'
                              ? 'bg-green-100 text-green-800 border-green-200'
                              : status === 'Abandoned'
                              ? 'bg-red-100 text-red-800 border-red-200'
                              : status === 'Expired'
                              ? 'bg-orange-100 text-orange-800 border-orange-200'
                              : 'bg-blue-100 text-blue-800 border-blue-200'
                          }
                        >
                          {status}
                        </Badge>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleRefreshApplication(resource.number);
                          }}
                          disabled={refreshingApps.has(resource.number)}
                          title="Refresh application data from USPTO"
                        >
                          <RefreshCw className={`w-3 h-3 ${refreshingApps.has(resource.number) ? 'animate-spin' : ''}`} />
                        </Button>
                      </div>

                      <h3 className="font-medium text-sm text-gray-900 mb-1 line-clamp-2">
                        {highlightText(getResourceTitle(resource), searchQuery)}
                      </h3>

                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">App. No.</span> {highlightText(resource.number, searchQuery)}
                      </p>

                      {/* Show additional searchable info when searching */}
                      {searchQuery && (
                        <div className="text-xs text-gray-600 space-y-1 mt-2">
                          {metaData?.firstInventorName && metaData.firstInventorName !== 'Loading...' && (
                            <div className="truncate">
                              <span className="font-medium">Inventor:</span> {highlightText(metaData.firstInventorName, searchQuery)}
                            </div>
                          )}
                          {metaData?.docketNumber && (
                            <div className="truncate">
                              <span className="font-medium">Docket:</span> {highlightText(metaData.docketNumber, searchQuery)}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="p-4 text-center text-gray-600">
                {searchQuery || filterStatus !== 'all' ? (
                  <div>
                    <p className="text-sm mb-2">No applications match your filters</p>
                    <p className="text-xs opacity-75">Try adjusting your search or filter</p>
                  </div>
                ) : (
                  <p className="text-sm">No applications available</p>
                )}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Main Content Area */}
      <div className="flex-1 h-full overflow-y-auto bg-gray-50">
        {selectedResource ? (
          <div className="p-6">
            <div className="mb-6">
              <div className="flex items-center gap-3 mb-2">
                <Badge
                  variant="outline"
                  className={
                    getResourceStatus(selectedResource) === 'Granted'
                      ? 'bg-green-100 text-green-800 border-green-200'
                      : getResourceStatus(selectedResource) === 'Abandoned'
                      ? 'bg-red-100 text-red-800 border-red-200'
                      : getResourceStatus(selectedResource) === 'Expired'
                      ? 'bg-orange-100 text-orange-800 border-orange-200'
                      : 'bg-blue-100 text-blue-800 border-blue-200'
                  }
                >
                  US APPLICATION
                </Badge>
                <h1 className="text-2xl font-bold text-gray-900">
                  {getResourceTitle(selectedResource)}
                </h1>
              </div>
              <div className="flex items-center gap-3">
                <p className="">
                  <span className="font-semibold">App. No.</span> {selectedResource.number}
                </p>
              {selectedResource.data?.patent_info?.applicationMetaData?.docketNumber && (
                <div className="truncate">
                  <span className="font-medium">Docket No.</span> {highlightText(selectedResource.data?.patent_info?.applicationMetaData?.docketNumber, searchQuery)}
                </div>
              )}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleRefreshApplication(selectedResource.number)}
                  disabled={refreshingApps.has(selectedResource.number)}
                  className="h-7 text-xs gap-1"
                  title="Refresh application data from USPTO"
                >
                  <RefreshCw className={`w-3 h-3 ${refreshingApps.has(selectedResource.number) ? 'animate-spin' : ''}`} />
                  {refreshingApps.has(selectedResource.number) ? 'Refreshing...' : 'Refresh'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => addToActiveDocuments(selectedResource.number)}
                  className="h-7 text-xs gap-1"
                >
                  <Plus className="w-3 h-3" />
                  Add to Active Documents
                </Button>
              </div>
            </div>

            <USApplicationDisplay applicationData={selectedResource.data} onDocumentClick={onDocumentSelect} />
          </div>
        ) : (
          <div className="h-full flex items-center justify-center">
            <div className="text-center">
              <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Select an Application</h3>
              <p className="text-sm text-gray-500">
                Choose an application from the sidebar to view details
              </p>
            </div>
          </div>
        )}
      </div>

      {/* Export Modal */}
      <ExportModal
        open={showExportModal}
        onOpenChange={setShowExportModal}
        applications={usptoResources.map(r => ({
          application_id: r.id,
          application_number: r.number,
          application_data: r.data.patent_info
        }))}
      />
    </div>
  );
}
